import * as React from 'react';
import { Link, graphql, useStaticQuery } from 'gatsby';
import { CookieNotice } from 'gatsby-cookie-notice';
import { library, icon } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from '@reach/router';
import Logo from '../assets/logo.svg';
import Footer from './footer';
import ServicesIcon from '../assets/services-icon.svg';
import ReferencesIcon from '../assets/references-icon.svg';
import UseCasesIcon from '../assets/use-cases-icon.svg';
import AboutUsIcon from '../assets/about-us-icon.svg';
import JobsIcon from '../assets/jobs-icon.svg';
import ProductsIcon from '../assets/products-icon.svg';
import BlogIcon from '../assets/blog-icon.svg';
// import SearchIcon from '../assets/search-icon.svg';
import ContactIcon from '../assets/contact-icon.svg';
import ChevronIcon from '../assets/chevron-icon.svg';
import LayoutConsumer, { LayoutProvider } from './layout-context';
import { isBrowser } from '../lib/utils';
import './storyblok/storyblok-bridge';
import * as icons from '../icons/icon-pack';
import Header from './header';
import { extractLanguage } from '../helpers/common';

// useWindowSize Hook
export function useWindowSize() {
  const [windowSize, setWindowSize] = React.useState({
    width: undefined,
    height: undefined,
  });

  React.useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize, { passive: true });
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

function Layout({ children }) {
  library.add({ ...icons });

  const location = useLocation();

  const language = extractLanguage(location.pathname);

  const siteData = useStaticQuery(graphql`
    query SiteMetadataQuery {
      site {
        siteMetadata {
          title
          consent {
            description
          }
        }
      }
      footer: allStoryblokEntry(filter: {name: {eq: "Footer"}}) {
        edges {
          node {
            name
            slug
            lang
            content
            field_component
            first_published_at
            full_slug
            uuid
          }
        }
        pageInfo {
          currentPage
          pageCount
        }
      }
    }
  `);

  const cookieInfo = siteData.footer.edges.find(({ node }) => node.lang === language);
  const cookieData = JSON.parse(cookieInfo.node.content);

  // eslint-disable-next-line max-len
  const [isNavOpen, setIsNavOpen] = React.useReducer((state) => !state, false); // initiate isNavOpen state with false
  // eslint-disable-next-line no-unused-vars
  const [isNavOpenDesktop, setIsNavOpenDesktop] = React.useState(() => {
    if (typeof localStorage === 'undefined') {
      return true;
    }
    return localStorage.getItem('menuOpen') !== 'false';
  });

  const windowSize = useWindowSize();

  const links = [
    {
      label: 'Leistungen',
      url: '/services',
      icon: <ServicesIcon className="w-8 h-8" />,
    },
    {
      label: 'Referenzen',
      url: '/references',
      icon: <ReferencesIcon className="w-8 h-8" />,
    },
    {
      label: 'Use Cases',
      url: '/use-cases',
      icon: <UseCasesIcon className="w-8 h-8" />,
    },
    {
      label: 'Apps',
      url: '/products',
      icon: <ProductsIcon className="w-8 h-8" />,
    },
    {
      label: 'Über Uns',
      url: '/about-us',
      icon: <AboutUsIcon className="w-8 h-8" />,
    },
    { label: 'Jobs', url: '/jobs', icon: <JobsIcon className="w-8 h-8" /> },
    { label: 'Blog', url: '/blog', icon: <BlogIcon className="w-8 h-8" /> },
    // { label: 'Search', url: '/search', icon: (<SearchIcon className="w-8 h-6" />) },
    {
      label: 'Kontakt',
      url: '/contact',
      icon: <ContactIcon className="w-8 h-8" />,
    },
  ];

  // const handleDesktopNavToggle = () => {
  //   setIsNavOpenDesktop((prev) => {
  //     let _prev = prev;
  //     if (_prev == null) {
  //       _prev = document.body.classList.contains('desktop-menu-open');
  //     }
  //     window.__setMenuOpen(!_prev);
  //     return !_prev;
  //   });

  //   // conversion tracking Google Analytics
  //   if (typeof window.gtag !== 'undefined') {
  //     window.gtag('event', 'collapse_sidebar');
  //   }
  // };

  const handleNavToggle = () => {
    if (windowSize.width >= 1473) return;
    setIsNavOpen((prev) => !prev);
    window.__setBodyScrollLock(!isNavOpen);
  };

  return (
    <LayoutProvider>
      <LayoutConsumer>
        {({ data }) => (
          <>
            <div className="dark">
              <div id="ProvenExpert_widgetbar_container" />
              <nav
                role="navigation"
                className="menu transition-width group z-50 fixed shadow-md xll:shadow-none p-4 xll:p-0 xll:pt-16 flex items-center justify-between w-full xll:h-full xll:block dark:text-white bg-gradient-to-b dark:from-[#050505] dark:to-[#171717] xll:bg-gradient-to-r from-[#FFFFFF] to-[#DDDDDD] dark:xll:from-[#050505] dark:xll:to-[#001C0A] xll:overflow-y-scroll no-scrollbar"
              >
                <Link
                  to={`${language === 'en' ? '/en' : '/'}`}
                  className="flex flex-col items-center justify-center w-fit xll:m-auto"
                >
                  <Logo className="h-8 transition-all logo" />
                  <span className="hidden mt-1 text-sm transition-all xll:block">
                    {siteData.site.siteMetadata.title}
                  </span>
                </Link>
                <Link
                  to="/"
                  className="relative z-0 text-xl xll:hidden sm:text-2xl left-4"
                >
                  {siteData.site.siteMetadata.title}
                </Link>
                <div className="flex">
                  <Link
                    to={`${language === 'en' ? '/en/contact' : '/contact'}`}
                    className="relative px-1 z-0 text-2xl xll:hidden right-4"
                  >
                    <FontAwesomeIcon icon={icon(icons.faMessages)} />
                  </Link>
                  <div className="menu-container transition-all h-[calc(100%-5rem)] xll:flex justify-between flex-col min-w-[32px]">
                    <div>
                      <div
                        aria-expanded={isNavOpen}
                        role="button"
                        className="fixed z-10 flex cursor-pointer xll:hidden top-5 right-4"
                        onClick={handleNavToggle}
                        onKeyPress={handleNavToggle}
                        tabIndex="0"
                      >
                        <div
                          id="mobile-menu-toggle"
                          className={isNavOpen ? 'open' : ''}
                        >
                          <span />
                          <span />
                          <span />
                          <span />
                        </div>
                      </div>
                      <div
                        className={`fixed xll:top-16 xll:left-0 w-full xll:w-full h-full xll:h-full xll:block xll:static transition-all overflow-hidden overflow-y-scroll xll:overflow-visible xll:bg-transparent dark:xll:bg-transparent pt-12 xll:pt-0 top-0 right-0 before:content-[attr(before)] before:absolute before:w-full before:right-0 before:h-full bg-gradient-to-r from-[#FFFFFF] to-[#DDDDDD] dark:from-[#050505] dark:to-[#001C0A] no-scrollbar before:transition-all before:duration-200 xll:opacity-100 xll:pointer-events-auto before:xll:static ${
                          isNavOpen
                            ? 'opacity-100 before:top-0 before:right-0 pointer-events-auto h-full'
                            : 'opacity-0 before:!-top-full before:!-right-full pointer-events-none'
                        }`}
                      >
                        <Header setIsNavOpen={setIsNavOpen} />
                      </div>
                    </div>
                  </div>
                </div>
              </nav>
            </div>

            <main className="main transform-gpu pt-16 lg:pt-0 bg:white dark:bg-background dark:text-white transition-all min-h-[80vh]">
              {data.fullWidth ? (
                <div>{children}</div>
              ) : (
                <div className="container flex px-4 xll:px-8">
                  <div className="max-w-screen-lg mx-auto my-4 xll:my-16">
                    {children}
                  </div>
                </div>
              )}
            </main>
            <div className="dark">
              <Footer className="text-white footer transform-gpu" />
            </div>

            {isBrowser()
              && !window.location.search.includes('_storyblok_tk[token]=') && (
                <CookieNotice
                  backgroundWrapperClasses="cookie-notice fixed top-0 left-0 w-full h-full z-[999999] bg-[rgba(255,255,255,0.75)] backdrop-blur text-white md:flex md:justify-center md:items-center"
                  backgroundClasses="bg-black absolute md:static bottom-4 left-4 w-[calc(100%-2rem)] p-4 md:p-8 rounded-md shadow-lg md:w-full md:max-w-[600px] max-h-[calc(100vh-2rem)] overflow-x-scroll"
                  wrapperClasses="flex md:gap-4 flex-col md:flex-row justify-center align-center text-center md:justify-between"
                  personalizeButtonClasses="text-latori-green hover:bg-gray-100 text-xs font-monospace uppercase w-fit rounded p-2 mx-auto"
                  personalizeButtonText={cookieData.show_cookies}
                  acceptButtonText={cookieData.accept_all_cookie}
                  acceptButtonClasses="bg-latori-pink hover:bg-latori-green text-white px-[20px] py-[10px] font-monospace uppercase rounded"
                  declineButtonText={cookieData.refuse_cookies}
                  declineButtonClasses="text-latori-green hover:bg-gray-100 px-[20px] py-[10px] mb-2 md:mb-0 md:mr-4 font-monospace uppercase rounded"
                  personalizeValidationText={cookieData.save}
                  personalizeValidationClasses="bg-latori-pink hover:bg-latori-green text-white px-[20px] py-[10px] font-monospace uppercase rounded"
                  cookieListClasses="flex flex-col gap-4 mt-4"
                  cookieMapClasses="form-control text-sm md:text-base"
                  cookieTitleClasses="text-lg font-bold"
                  cookies={[
                    {
                      name: 'necessary',
                      editable: false,
                      default: true,
                      title: language === 'default' ? 'Essenziell' : 'Essential',
                      text: cookieData.essential_cookie,
                    },
                    {
                      name: 'gatsby-gdpr-google-analytics',
                      editable: true,
                      default:
                        typeof localStorage !== 'undefined'
                        && localStorage?.getItem(
                          'gatsby-gdpr-google-analytics',
                        ) === 'true',
                      title: 'Google Analytics',
                      text: cookieData.google_analytics_cookie,
                    },
                    {
                      name: 'gatsby-gdpr-klaviyo',
                      editable: true,
                      default:
                        typeof localStorage !== 'undefined'
                        && localStorage?.getItem('gatsby-gdpr-klaviyo') === 'true',
                      title: 'Klaviyo',
                      text: cookieData.klaviyo_cookie,
                    },
                    {
                      name: 'gatsby-gdpr-proven-expert',
                      editable: true,
                      default:
                        typeof localStorage !== 'undefined'
                        && localStorage?.getItem('gatsby-gdpr-proven-expert')
                          === 'true',
                      title: 'ProvenExpert',
                      text: cookieData.proven_expert_cookie,
                    },
                    {
                      name: 'gatsby-gdpr-linked-in',
                      editable: true,
                      default:
                        typeof localStorage !== 'undefined'
                        && localStorage?.getItem('gatsby-gdpr-linked-in')
                          === 'true',
                      title: 'LinkedIn',
                      text: cookieData.linkedin_cookie,
                    },
                    {
                      name: 'gatsby-gdpr-facebook-pixel',
                      editable: true,
                      default:
                        typeof localStorage !== 'undefined'
                        && localStorage?.getItem('gatsby-gdpr-facebook-pixel')
                          === 'true',
                      title: 'Facebook Pixel',
                      text: cookieData.facebook_pixel_cookie,
                    },
                    {
                      name: 'gatsby-gdpr-inspectlet-pixel',
                      editable: true,
                      default:
                        typeof localStorage !== 'undefined'
                        && localStorage?.getItem(
                          'gatsby-gdpr-inspectlet-pixel',
                        ) === 'true',
                      title: 'Inspectlet Pixel',
                      text: cookieData.inspectlet_pixel_cookie,
                    },
                  ]}
                >
                  <h4 className="mb-2 text-xl font-bold">
                    {siteData.site.siteMetadata.consent.title}
                  </h4>
                  <p className="text-sm md:text-base">
                    {cookieData.cookie_description}
                  </p>
                  <div className="flex flex-wrap justify-center mx-auto my-4 text-center">
                    <Link
                      className="text-latori-green text-xs py-[10px] font-monospace uppercase mr-4"
                      to="/imprint"
                    >
                      {siteData.site.siteMetadata.consent.imprint}
                    </Link>
                    <Link
                      className="text-latori-green text-xs py-[10px] font-monospace uppercase"
                      to="/privacy"
                    >
                      {siteData.site.siteMetadata.consent.privacy}
                    </Link>
                  </div>
                </CookieNotice>
            )}

            <Link
              to="/blogpost/shop-usability-award"
              className="fixed p-2 bottom-12 right-4 xll:bottom-8 xll:right-4 xll:opacity-25 hover:opacity-100 xll:p-2 transition-opacity duration-150 ease-linear"
            >
              <img
                src="/assets/images/logo-shop-usability-award-best-agency.png"
                alt="Shop Usability Award Winner 2023"
                loading="lazy"
                className="w-32 xll:w-48"
              />
            </Link>
          </>
        )}
      </LayoutConsumer>
    </LayoutProvider>
  );
}

export default Layout;
